/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Heading } from '@chakra-ui/layout'
import {
    Box,
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    SimpleGrid,
    Spinner,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { optionsDefault } from 'components/molecules/SelectBuild/constants'
import { LocationsDTO } from 'models'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { getMunicipio } from 'services/municipio'
import { getCEP } from 'services/viacep'
import formatter from 'utils/formatter'
import { removeDuplicateCities } from 'utils/removeDuplicateCities'
import { useDebounce } from 'utils/useDebounce'
import { attributesAddressToReset, fieldsAddress } from './constants'
import { FormAgents } from './form'
import {
    DadosNotaDebitoFormValues,
    FieldAddress,
    FormAgentElementsErrors,
    FormAgentElementsValues,
    FormAgentProps
} from './interfaces'
import { schema } from './schema'

type DadosCadastraisAgente = Partial<
    Pick<
        FormAgentElementsValues,
        | 'razaoSocial'
        | 'cnpj'
        | 'telefone'
        | 'logradouro'
        | 'numeroEndereco'
        | 'complemento'
        | 'bairro'
        | 'municipio'
        | 'codigoMunicipio'
        | 'ufEndereco'
        | 'uf'
        | 'cep'
    >
>

const dadosNotaDebitoDefault: DadosNotaDebitoFormValues = {
    razaoSocial: '',
    cnpj: '',
    inscricaoEstadual: '',
    telefone: '',
    logradouro: '',
    numeroEndereco: '',
    complemento: '',
    bairro: '',
    municipio: '',
    codigoMunicipio: '',
    uf: '',
    cep: ''
}

const somenteNumeros = (valor?: string | null): string => {
    return valor?.replace(/\D/g, '') ?? ''
}

const formatarCEP = (valor?: string | null): string => {
    const numeros = somenteNumeros(valor).slice(0, 8)

    if (numeros.length <= 5) {
        return numeros
    }

    return `${numeros.slice(0, 5)}-${numeros.slice(5)}`
}

const normalizarDadosNotaDebito = (
    dados: DadosNotaDebitoFormValues
): DadosNotaDebitoFormValues => {
    return {
        razaoSocial: dados.razaoSocial?.trim() ?? '',
        cnpj: somenteNumeros(dados.cnpj),
        inscricaoEstadual:
            dados.inscricaoEstadual?.trim() || undefined,
        telefone: somenteNumeros(dados.telefone),
        logradouro: dados.logradouro?.trim() ?? '',
        numeroEndereco: dados.numeroEndereco?.trim() ?? '',
        complemento: dados.complemento?.trim() || undefined,
        bairro: dados.bairro?.trim() ?? '',
        municipio: dados.municipio?.trim() ?? '',
        codigoMunicipio:
            dados.codigoMunicipio?.trim() || undefined,
        uf: dados.uf ?? '',
        cep: somenteNumeros(dados.cep)
    }
}

const FormAgent: React.FC<FormAgentProps> = ({
    onSubmitForm,
    onCancelForm,
    itemEdit
}) => {
    const [location, setLocation] = useState<LocationsDTO[]>([])
    const [searching, setSearching] = useState(false)
    const [searchingNotaDebito, setSearchingNotaDebito] =
        useState(false)

    const toast = useToast()

    const {
        handleSubmit,
        register,
        watch,
        getValues,
        formState: { errors, isSubmitting },
        setValue
    } = useForm<FormAgentElementsValues>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            dadosNotaDebito: dadosNotaDebitoDefault
        }
    })

    const ufDetran = watch('uf')
    const cepNotaDebito = watch('dadosNotaDebito.cep') ?? ''
    const form = FormAgents(ufDetran, location)

    const prepareMunicipio = (
        localidade: string,
        locations: LocationsDTO[]
    ): string => {
        if (!localidade || !locations) {
            return ''
        }

        const normalizedLocalidade = formatter
            .clearCharacters(localidade)
            .toUpperCase()

        const municipioEntry = locations.find(
            ({ municipio }) =>
                municipio === normalizedLocalidade
        )

        return municipioEntry
            ? `${municipioEntry.codigoTomDetran}|${normalizedLocalidade}`
            : ''
    }

    const localizarMunicipio = (
        localidade: string,
        locations: LocationsDTO[]
    ): LocationsDTO | undefined => {
        if (!localidade || !locations) {
            return undefined
        }

        const normalizedLocalidade = formatter
            .clearCharacters(localidade)
            .toUpperCase()

        return locations.find(
            ({ municipio }) =>
                municipio === normalizedLocalidade
        )
    }

    const buscarMunicipios = async (
        uf: string
    ): Promise<LocationsDTO[]> => {
        if (!uf) {
            return []
        }

        const { data } = await getMunicipio(uf)

        return removeDuplicateCities(data)
    }

    const consultMunicipio = async (
        uf: string
    ): Promise<LocationsDTO[]> => {
        if (!uf) {
            setLocation([])
            return []
        }

        try {
            setSearching(true)

            const locations = await buscarMunicipios(uf)

            setLocation(locations)

            return locations
        } finally {
            setSearching(false)
        }
    }

    const setValuesCep = (
        address: Record<FieldAddress, string | undefined>
    ) => {
        fieldsAddress.forEach((field: FieldAddress) => {
            setValue(field, address[field] ?? '')
        })
    }

    const clearAddress = () => {
        Object.keys(attributesAddressToReset).forEach(
            (key: string) => {
                setValue(key as FieldAddress, '')
            }
        )
    }

    const consultCEP = useDebounce(async (cep: string) => {
        if (cep.length !== 8) {
            clearAddress()
            setSearching(false)
            return
        }

        try {
            setSearching(true)

            const response = await getCEP(cep)

            const {
                cep: fetchedCep,
                localidade,
                logradouro,
                bairro,
                uf
            } = response

            const locations = await consultMunicipio(uf)

            setTimeout(() => {
                const municipio = prepareMunicipio(
                    localidade,
                    locations ?? []
                )

                setValuesCep({
                    cep: fetchedCep,
                    ufEndereco: uf,
                    municipio,
                    logradouro,
                    bairro
                })
            }, 200)
        } catch (error) {
            console.error('CEP lookup failed:', error)

            setValuesCep({
                cep: '',
                ...attributesAddressToReset
            })
        } finally {
            setSearching(false)
        }
    }, 300)

    const handleCEP = (cep: string) => {
        const value = somenteNumeros(cep)

        setSearching(value.length === 8)
        consultCEP(value)
    }

    const limparEnderecoNotaDebito = () => {
        setValue('dadosNotaDebito.uf', '')
        setValue('dadosNotaDebito.municipio', '')
        setValue('dadosNotaDebito.codigoMunicipio', '')
        setValue('dadosNotaDebito.logradouro', '')
        setValue('dadosNotaDebito.bairro', '')
    }

    const preencherEnderecoNotaDebito = (
        cep: string,
        uf: string,
        municipio: string,
        codigoMunicipio: string,
        logradouro: string,
        bairro: string
    ) => {
        setValue(
            'dadosNotaDebito.cep',
            formatarCEP(cep),
            {
                shouldValidate: true,
                shouldDirty: true
            }
        )

        setValue('dadosNotaDebito.uf', uf ?? '', {
            shouldValidate: true,
            shouldDirty: true
        })

        setValue(
            'dadosNotaDebito.municipio',
            municipio ?? '',
            {
                shouldValidate: true,
                shouldDirty: true
            }
        )

        setValue(
            'dadosNotaDebito.codigoMunicipio',
            codigoMunicipio ?? '',
            {
                shouldValidate: true,
                shouldDirty: true
            }
        )

        setValue(
            'dadosNotaDebito.logradouro',
            logradouro ?? '',
            {
                shouldValidate: true,
                shouldDirty: true
            }
        )

        setValue(
            'dadosNotaDebito.bairro',
            bairro ?? '',
            {
                shouldValidate: true,
                shouldDirty: true
            }
        )
    }

    const consultCEPNotaDebito = useDebounce(
        async (cep: string) => {
            if (cep.length !== 8) {
                limparEnderecoNotaDebito()
                setSearchingNotaDebito(false)
                return
            }

            try {
                setSearchingNotaDebito(true)

                const response = await getCEP(cep)

                const {
                    cep: fetchedCep,
                    localidade,
                    logradouro,
                    bairro,
                    uf
                } = response

                const locations = await buscarMunicipios(uf)

                const municipioEncontrado = localizarMunicipio(
                    localidade,
                    locations
                )

                preencherEnderecoNotaDebito(
                    fetchedCep,
                    uf,
                    localidade,
                    String(
                        municipioEncontrado?.codigoTomDetran ?? ''
                    ),
                    logradouro,
                    bairro
                )
            } catch (error) {
                console.error(
                    'CEP da Nota de Débito não encontrado:',
                    error
                )

                limparEnderecoNotaDebito()

                toast({
                    title: 'CEP não encontrado',
                    description:
                        'Não foi possível localizar o endereço da Nota de Débito. Verifique o CEP informado.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            } finally {
                setSearchingNotaDebito(false)
            }
        },
        300
    )

    const handleCEPNotaDebito = (valor: string) => {
        const cep = somenteNumeros(valor).slice(0, 8)
        const cepFormatado = formatarCEP(cep)

        setValue(
            'dadosNotaDebito.cep',
            cepFormatado,
            {
                shouldDirty: true,
                shouldValidate: false
            }
        )

        setSearchingNotaDebito(cep.length === 8)
        consultCEPNotaDebito(cep)
    }

    const setMunicipio = async () => {
        if (!itemEdit) {
            return
        }

        const ufEndereco = itemEdit.ufEndereco
        const locations = await consultMunicipio(ufEndereco)

        setTimeout(() => {
            if (locations?.length) {
                const municipio = prepareMunicipio(
                    itemEdit.municipio,
                    locations
                )

                setValue('municipio', municipio)
            }
        }, 100)
    }

    const criarDadosNotaDebitoPadrao = (
        agente: DadosCadastraisAgente
    ): DadosNotaDebitoFormValues => {
        const municipioAgente = agente.municipio ?? ''
        const municipioSeparado = municipioAgente.split('|')

        return {
            razaoSocial: agente.razaoSocial ?? '',
            cnpj: agente.cnpj ?? '',
            inscricaoEstadual: '',
            telefone: agente.telefone ?? '',
            logradouro: agente.logradouro ?? '',
            numeroEndereco: agente.numeroEndereco ?? '',
            complemento: agente.complemento ?? '',
            bairro: agente.bairro ?? '',
            municipio:
                municipioSeparado.length > 1
                    ? municipioSeparado.slice(1).join('|')
                    : municipioAgente,
            codigoMunicipio:
                municipioSeparado.length > 1
                    ? municipioSeparado[0]
                    : agente.codigoMunicipio ?? '',
            uf: agente.ufEndereco || agente.uf || '',
            cep: formatarCEP(agente.cep)
        }
    }

    const copiarDadosCadastrais = () => {
        const values: DadosCadastraisAgente = getValues()

        const dadosAtuais = getValues('dadosNotaDebito')

        const dadosNotaDebito =
            criarDadosNotaDebitoPadrao(values)

        setValue(
            'dadosNotaDebito',
            {
                ...dadosNotaDebito,

                // O campo permanece oculto, mas seu valor atual
                // não deve ser apagado ao copiar o endereço.
                inscricaoEstadual:
                    dadosAtuais?.inscricaoEstadual ?? ''
            },
            {
                shouldValidate: true,
                shouldDirty: true
            }
        )
    }

    const setValuesToEdit = () => {
        if (!itemEdit) {
            return
        }

        Object.entries(itemEdit).forEach(
            ([key, value]: [string, any]) => {
                if (
                    key === 'dadosNotaDebito' ||
                    key === 'id'
                ) {
                    return
                }

                const optionsValues = {
                    categorizacao:
                        optionsDefault.agenteCategorizacao.find(
                            cat => cat.text === value
                        )?.value ?? value,

                    instituicaoPagaTaxa:
                        key === 'instituicaoPagaTaxa'
                            ? value
                                ? 'SIM'
                                : 'NAO'
                            : value,

                    dataInicioVigencia:
                        key === 'dataInicioVigencia' && value
                            ? formatter.formatDateToEn(value)
                            : value,

                    dataFimVigencia:
                        key === 'dataFimVigencia' && value
                            ? formatter.formatDateToEn(value)
                            : value
                }

                setValue(
                    key as any,
                    optionsValues[
                        key as keyof typeof optionsValues
                    ] ?? value
                )
            }
        )

        const dadosNotaDebito =
            itemEdit.dadosNotaDebito ??
            criarDadosNotaDebitoPadrao(itemEdit)

        setValue('dadosNotaDebito', {
            ...dadosNotaDebito,
            cep: formatarCEP(dadosNotaDebito.cep)
        })

        setMunicipio()
    }

    useEffect(() => {
        if (itemEdit?.id) {
            setValuesToEdit()
        }
    }, [itemEdit?.id])

    const onSubmit = (
        value: FormAgentElementsValues
    ): void => {
        onSubmitForm({
            data: {
                ...value,
                dadosNotaDebito: normalizarDadosNotaDebito(
                    value.dadosNotaDebito
                )
            }
        })
    }

    const onError = (value: unknown): void => {
        onSubmitForm({
            errors: value as FormAgentElementsErrors
        })
    }

    const onCancel = () => {
        onCancelForm?.()
    }

    const registrarSelect = (id: string) => {
        return register(id as any, {
            onChange: event => {
                if (id === 'ufEndereco') {
                    setValue('municipio', '')
                    consultMunicipio(event.target.value)
                }
            }
        })
    }

    return (
        <Box overflowY="scroll" maxH="550px">
            <form
                onSubmit={handleSubmit(onSubmit, onError)}
                data-testid="form"
            >
                <Layout
                    type="container"
                    px={8}
                    full
                    display="grid"
                    gap={8}
                    data-testid="form-container"
                >
                    <Layout type="row" gutter={{ y: 1.5 }}>
                        {form.map(
                            (
                                { layout, input, select }: any,
                                key: number
                            ) => (
                                <Layout {...layout} key={key}>
                                    {select ? (
                                        <SelectBuild
                                            {...select}
                                            err={
                                                errors[
                                                    select.id as keyof FormAgentElementsValues
                                                ]
                                            }
                                            {...registrarSelect(
                                                select.id
                                            )}
                                            isSearching={
                                                select.isSearching &&
                                                searching
                                            }
                                            value={
                                                watch(
                                                    select.id as any
                                                ) as any
                                            }
                                        />
                                    ) : (
                                        input && (
                                            <InputBuild
                                                {...input}
                                                err={
                                                    errors[
                                                        input.id as keyof FormAgentElementsValues
                                                    ]
                                                }
                                                {...register(
                                                    input.id as any
                                                )}
                                                value={
                                                    watch(
                                                        input.id as any
                                                    ) as any
                                                }
                                                isSearching={
                                                    input.isSearching &&
                                                    searching
                                                }
                                                onKeyUp={(
                                                    event: React.KeyboardEvent<HTMLInputElement>
                                                ) => {
                                                    input.onKeyUp?.(
                                                        event
                                                    )

                                                    if (
                                                        input.id ===
                                                        'cep'
                                                    ) {
                                                        handleCEP(
                                                            event
                                                                .currentTarget
                                                                .value
                                                        )
                                                    }
                                                }}
                                            />
                                        )
                                    )}
                                </Layout>
                            )
                        )}
                    </Layout>

                    <Box
                        borderTop="1px solid"
                        borderColor="gray.200"
                        paddingTop={6}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            marginBottom={5}
                        >
                            <Heading size="sm">
                                Dados para emissão da Nota de Débito
                            </Heading>

                            <Button
                                type="button"
                                variant="outline"
                                colorScheme="primary"
                                size="sm"
                                onClick={copiarDadosCadastrais}
                            >
                                Copiar dados cadastrais
                            </Button>
                        </Box>

                        <SimpleGrid
                            columns={{
                                base: 1,
                                md: 2
                            }}
                            spacing={6}
                        >
                            <input
                                type="hidden"
                                {...register(
                                    'dadosNotaDebito.inscricaoEstadual'
                                )}
                            />

                            <input
                                type="hidden"
                                {...register(
                                    'dadosNotaDebito.codigoMunicipio'
                                )}
                            />

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito
                                        ?.razaoSocial
                                }
                            >
                                <FormLabel>Razão Social</FormLabel>

                                <Input
                                    maxLength={100}
                                    {...register(
                                        'dadosNotaDebito.razaoSocial'
                                    )}
                                />

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito
                                            ?.razaoSocial?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito?.cnpj
                                }
                            >
                                <FormLabel>CNPJ</FormLabel>

                                <Input
                                    maxLength={18}
                                    placeholder="00.000.000/0000-00"
                                    {...register(
                                        'dadosNotaDebito.cnpj'
                                    )}
                                />

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito?.cnpj
                                            ?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito
                                        ?.telefone
                                }
                            >
                                <FormLabel>Telefone</FormLabel>

                                <Input
                                    maxLength={15}
                                    placeholder="(00) 00000-0000"
                                    {...register(
                                        'dadosNotaDebito.telefone'
                                    )}
                                />

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito
                                            ?.telefone?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito?.cep
                                }
                            >
                                <FormLabel>CEP</FormLabel>

                                <InputGroup>
                                    <Input
                                        maxLength={9}
                                        placeholder="00000-000"
                                        {...register(
                                            'dadosNotaDebito.cep'
                                        )}
                                        value={cepNotaDebito}
                                        onChange={event =>
                                            handleCEPNotaDebito(
                                                event.target.value
                                            )
                                        }
                                    />

                                    {searchingNotaDebito && (
                                        <InputRightElement>
                                            <Spinner
                                                size="sm"
                                                color="primary.500"
                                            />
                                        </InputRightElement>
                                    )}
                                </InputGroup>

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito?.cep
                                            ?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito?.uf
                                }
                            >
                                <FormLabel>UF</FormLabel>

                                <Select
                                    placeholder="Selecione"
                                    {...register(
                                        'dadosNotaDebito.uf'
                                    )}
                                >
                                    {optionsDefault.ufContract.map(
                                        option => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.text}
                                            </option>
                                        )
                                    )}
                                </Select>

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito?.uf
                                            ?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito
                                        ?.municipio
                                }
                            >
                                <FormLabel>Município</FormLabel>

                                <Input
                                    maxLength={100}
                                    {...register(
                                        'dadosNotaDebito.municipio'
                                    )}
                                />

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito
                                            ?.municipio?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito
                                        ?.logradouro
                                }
                            >
                                <FormLabel>Logradouro</FormLabel>

                                <Input
                                    maxLength={150}
                                    {...register(
                                        'dadosNotaDebito.logradouro'
                                    )}
                                />

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito
                                            ?.logradouro?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito
                                        ?.numeroEndereco
                                }
                            >
                                <FormLabel>Número</FormLabel>

                                <Input
                                    maxLength={20}
                                    {...register(
                                        'dadosNotaDebito.numeroEndereco'
                                    )}
                                />

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito
                                            ?.numeroEndereco?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isRequired
                                isInvalid={
                                    !!errors.dadosNotaDebito?.bairro
                                }
                            >
                                <FormLabel>Bairro</FormLabel>

                                <Input
                                    maxLength={100}
                                    {...register(
                                        'dadosNotaDebito.bairro'
                                    )}
                                />

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito?.bairro
                                            ?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isInvalid={
                                    !!errors.dadosNotaDebito
                                        ?.complemento
                                }
                            >
                                <FormLabel>Complemento</FormLabel>

                                <Input
                                    maxLength={100}
                                    {...register(
                                        'dadosNotaDebito.complemento'
                                    )}
                                />

                                <FormErrorMessage>
                                    {
                                        errors.dadosNotaDebito
                                            ?.complemento?.message
                                    }
                                </FormErrorMessage>
                            </FormControl>
                        </SimpleGrid>
                    </Box>

                    <Layout
                        type="row"
                        data-testid="form-action"
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width="100%"
                            gap={4}
                        >
                            <Button
                                data-testid="form-action-cancel"
                                type="button"
                                variant="outline"
                                onClick={onCancel}
                            >
                                Cancelar
                            </Button>

                            <Button
                                data-testid="form-action-confirm"
                                colorScheme="primary"
                                type="submit"
                                isLoading={isSubmitting}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Layout>
                </Layout>
            </form>
        </Box>
    )
}

export default FormAgent